import { Popover, TextField } from "@mui/material";
import { useEffect, useState } from "react";

type ElementWithComments = {
  ElementId: number;
  Comments?: string | null;
};

type UpsertFunction = (
  elementId: number,
  field: string,
  value: unknown,
  ...args: unknown[]
) => Promise<{ ok: boolean; message?: string; data?: unknown }>;

type Props<T extends ElementWithComments> = {
  show: boolean;
  onHide: () => void;
  refreshData?: () => void | Promise<void>;
  position: { top: number; left: number } | undefined;
  selectedElement: T | null;
  setSelectedElement:
    | ((element: T | null) => void)
    | ((element: ElementWithComments | null) => void);
  upsertFunction: UpsertFunction;
  additionalArgs?: readonly number[];
  readOnly?: boolean;
};

export const CommentModal = <T extends ElementWithComments>({
  show,
  onHide,
  refreshData,
  position,
  selectedElement,
  setSelectedElement,
  upsertFunction,
  additionalArgs = [],
  readOnly = false,
}: Props<T>) => {
  const [comment, setComment] = useState("");
  const [commentToUpsert, setCommentToUpsert] = useState("");

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (/^(?!\s*$).+/.test(e.target.value)) {
      setCommentToUpsert(e.target.value);
    }
    setComment(e.target.value);
  };

  const onClose = async () => {
    if (
      !readOnly &&
      commentToUpsert !== selectedElement?.Comments &&
      commentToUpsert !== ""
    ) {
      if (selectedElement?.ElementId) {
        await upsertFunction(
          selectedElement.ElementId,
          "Comments",
          commentToUpsert,
          ...additionalArgs,
        );
      }
      if (refreshData) await refreshData();
    }
    setComment("");
    setCommentToUpsert("");
    onHide();
    setSelectedElement(null);
  };

  useEffect(() => {
    if (selectedElement) {
      setComment(selectedElement.Comments ?? "");
      setCommentToUpsert(selectedElement.Comments ?? "");
    }
  }, [selectedElement]);

  return (
    <Popover
      open={show}
      onClose={() => onClose()}
      anchorReference="anchorPosition"
      anchorPosition={position}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            padding: 0,
            overflow: "hidden",
          },
        },
      }}
    >
      <TextField
        multiline
        minRows={6}
        variant="outlined"
        fullWidth
        placeholder="Enter the comments here"
        value={comment}
        onChange={onChange}
        disabled={readOnly}
        sx={{
          width: "508px",
          margin: 0,
          "& .MuiOutlinedInput-root": {
            padding: 0,
            borderRadius: 0,
          },
          "& .MuiInputBase-input": {
            padding: "8px",
          },
          "& fieldset": {
            borderRadius: 0,
          },
        }}
      />
    </Popover>
  );
};
